import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { agentManagementService } from '../../services/agentManagementService';
import { useOpenAI } from '../../hooks/useOpenAI';
import AppIcon from '../../components/AppIcon';

const EmbedChatWidget = () => {
  const { agentId } = useParams();
  
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [agent, setAgent] = useState(null);
  const [streamingMessage, setStreamingMessage] = useState('');
  const [error, setError] = useState(null);
  const [isLoadingAgent, setIsLoadingAgent] = useState(true);
  const [appliedStyling, setAppliedStyling] = useState(null);

  // Detect if we're in an iframe for proper layout
  const isInIframe = window !== window.parent;

  // OpenAI Integration with updated configuration
  const {
    sendMessage,
    sendStreamingMessage,
    testConnection,
    isLoading: isTyping,
    isStreaming,
    error: openaiError,
    lastResponse,
    config: openaiConfig,
    updateConfig: updateOpenAIConfig,
    clearError,
    isConnected: openaiConnected
  } = useOpenAI({
    model: 'gpt-4',
    systemPrompt: 'Du bist ein hilfreicher AI-Assistent. Antworte auf Deutsch und sei freundlich und professionell.',
    streamEnabled: true,
    reasoningEffort: 'medium',
    verbosity: 'medium'
  });

  // Default agent configuration with German text
  const getDefaultAgent = () => ({
    id: agentId || 'default',
    name: 'AI Assistent',
    display_name: 'AI Assistent',
    tagline: 'Was können wir für Sie tun?',
    model: 'gpt-4',
    provider: 'openai',
    system_prompt: 'Du bist ein hilfreicher AI-Assistent. Antworte auf Deutsch und sei freundlich und professionell.',
    welcome_message: 'Hi, Was können wir für Sie tun?',
    conversation_starters: [
      'Eine Frage stellen',
      'Unser Bot und unser Team können helfen',
      'Informationen anfordern',
      'Unterstützung benötigen'
    ],
    appearance: {
      widgetSize: 'medium',
      bubbleIcon: 'MessageCircle',
      bubbleColor: '#21b17a',
      botAvatar: null,
      userAvatar: null,
      fontSize: '14',
      fontFamily: 'system',
      colorScheme: {
        primary: '#21b17a',
        primaryHover: '#1a8f65',
        accent: '#3b82f6',
        chatBackground: '#000000',
        messageBackground: '#1f2937',
        userMessageBackground: '#21b17a',
        textPrimary: '#ffffff',
        textSecondary: '#d1d5db',
        textMuted: '#9ca3af',
        border: '#374151',
        borderLight: '#4b5563',
        divider: '#374151'
      },
      taglineText: 'Was können wir für Sie tun?',
      showTagline: true,
      onlineMessage: 'Wir sind online und bereit zu helfen!',
      offlineMessage: 'Wir sind momentan offline',
      enableTyping: true,
      typingSpeed: 'medium',
      showTypingIndicator: true
    },
    streaming_enabled: true,
    temperature: 0.67
  });

  // Process agent appearance settings and apply styling
  const processAgentStyling = (agentData) => {
    if (!agentData?.appearance) return null;
    
    const appearance = agentData?.appearance;
    
    // Create CSS custom properties for dynamic theming
    const styling = {
      // Color scheme
      primaryColor: appearance?.colorScheme?.primary || appearance?.bubbleColor || '#21b17a',
      primaryHover: appearance?.colorScheme?.primaryHover || '#1a8f65',
      accentColor: appearance?.colorScheme?.accent || '#3b82f6',
      chatBackground: appearance?.colorScheme?.chatBackground || '#000000',
      messageBackground: appearance?.colorScheme?.messageBackground || '#1f2937',
      userMessageBackground: appearance?.colorScheme?.userMessageBackground || appearance?.colorScheme?.primary || '#21b17a',
      textPrimary: appearance?.colorScheme?.textPrimary || '#ffffff',
      textSecondary: appearance?.colorScheme?.textSecondary || '#d1d5db',
      textMuted: appearance?.colorScheme?.textMuted || '#9ca3af',
      borderColor: appearance?.colorScheme?.border || '#374151',
      borderLight: appearance?.colorScheme?.borderLight || '#4b5563',
      divider: appearance?.colorScheme?.divider || '#374151',
      
      // Typography
      fontSize: appearance?.fontSize || '14',
      fontFamily: appearance?.fontFamily || 'system',
      
      // Widget settings
      widgetSize: appearance?.widgetSize || 'medium',
      bubbleIcon: appearance?.bubbleIcon || 'MessageCircle',
      
      // Avatars
      botAvatar: appearance?.botAvatar,
      userAvatar: appearance?.userAvatar,
      
      // Content
      showTagline: appearance?.showTagline !== false,
      taglineText: appearance?.taglineText || agentData?.tagline || 'Was können wir für Sie tun?',
      
      // Behavior
      enableTyping: appearance?.enableTyping !== false,
      showTypingIndicator: appearance?.showTypingIndicator !== false
    };
    
    return styling;
  };

  // Apply dynamic CSS custom properties
  const applyDynamicStyling = (styling) => {
    if (!styling) return;
    
    const root = document.documentElement;
    
    // Apply CSS custom properties for theming
    root.style?.setProperty('--embed-primary-color', styling?.primaryColor);
    root.style?.setProperty('--embed-primary-hover', styling?.primaryHover);
    root.style?.setProperty('--embed-accent-color', styling?.accentColor);
    root.style?.setProperty('--embed-chat-bg', styling?.chatBackground);
    root.style?.setProperty('--embed-message-bg', styling?.messageBackground);
    root.style?.setProperty('--embed-user-message-bg', styling?.userMessageBackground);
    root.style?.setProperty('--embed-text-primary', styling?.textPrimary);
    root.style?.setProperty('--embed-text-secondary', styling?.textSecondary);
    root.style?.setProperty('--embed-text-muted', styling?.textMuted);
    root.style?.setProperty('--embed-border', styling?.borderColor);
    root.style?.setProperty('--embed-border-light', styling?.borderLight);
    root.style?.setProperty('--embed-divider', styling?.divider);
    root.style?.setProperty('--embed-font-size', `${styling?.fontSize}px`);
    
    // Apply font family
    if (styling?.fontFamily && styling?.fontFamily !== 'system') {
      root.style?.setProperty('--embed-font-family', styling?.fontFamily);
    }
  };

  // Get CSS classes based on applied styling
  const getStyledClasses = (baseClass, elementType) => {
    if (!appliedStyling) {
      // Fallback to default dark theme
      const fallbackClasses = {
        chatBackground: 'bg-black',
        headerBackground: 'bg-black border-gray-700',
        messageBackground: 'bg-gray-800 text-white',
        userMessageBackground: 'bg-white text-black',
        inputBackground: 'bg-gray-900 border-gray-700 text-white placeholder-gray-400',
        buttonPrimary: 'bg-white text-black hover:bg-gray-100',
        textPrimary: 'text-white',
        textSecondary: 'text-gray-300',
        textMuted: 'text-gray-400',
        border: 'border-gray-700'
      };
      return `${baseClass} ${fallbackClasses?.[elementType] || ''}`;
    }
    
    // Use CSS custom properties for dynamic theming
    const styledClasses = {
      chatBackground: 'bg-[var(--embed-chat-bg)] text-[var(--embed-text-primary)]',
      headerBackground: 'bg-[var(--embed-chat-bg)] border-[var(--embed-border)]',
      messageBackground: 'bg-[var(--embed-message-bg)] text-[var(--embed-text-primary)]',
      userMessageBackground: 'bg-[var(--embed-user-message-bg)] text-white',
      inputBackground: 'bg-[var(--embed-message-bg)] border-[var(--embed-border)] text-[var(--embed-text-primary)] placeholder-[var(--embed-text-muted)]',
      buttonPrimary: 'bg-[var(--embed-primary-color)] text-white hover:bg-[var(--embed-primary-hover)]',
      textPrimary: 'text-[var(--embed-text-primary)]',
      textSecondary: 'text-[var(--embed-text-secondary)]',
      textMuted: 'text-[var(--embed-text-muted)]',
      border: 'border-[var(--embed-border)]'
    };
    
    return `${baseClass} ${styledClasses?.[elementType] || ''}`;
  };

  // Initialize the chat widget
  useEffect(() => {
    const initializeAgent = async () => {
      try {
        setError(null);
        setIsLoadingAgent(true);
        
        console.log(`🚀 EMBED: Initializing agent with ID: ${agentId}`);
        
        // Try to load agent configuration from database
        if (agentId && agentId !== 'default') {
          try {
            console.log(`📡 EMBED: Fetching agent data for ID: ${agentId}`);
            
            // Use public method for embed widgets
            const agentData = await agentManagementService?.getPublicAgentById(agentId);
            
            if (agentData) {
              console.log(`✅ EMBED: Agent data loaded successfully:`, {
                id: agentData?.id,
                name: agentData?.name,
                display_name: agentData?.display_name,
                system_prompt: agentData?.system_prompt ? 'Present' : 'Missing',
                conversation_starters: agentData?.conversation_starters?.length || 0,
                appearance: agentData?.appearance ? 'Custom styling available' : 'No custom styling'
              });
              
              // Process conversation starters - ensure it's an array
              let conversationStarters = [];
              if (agentData?.conversation_starters) {
                conversationStarters = Array.isArray(agentData?.conversation_starters) 
                  ? agentData?.conversation_starters 
                  : [];
              }

              // Build complete agent configuration
              const completeAgent = {
                ...agentData,
                conversation_starters: conversationStarters?.length > 0 ? conversationStarters : [
                  'Eine Frage stellen',
                  'Unser Bot und unser Team können helfen',
                  'Informationen anfordern'
                ],
                tagline: agentData?.appearance?.taglineText || 
                        agentData?.appearance?.tagline || 
                        agentManagementService?.generateTagline(agentData) || 
                        'Was können wir für Sie tun?',
                welcome_message: agentData?.initial_messages?.[0] || 
                                agentData?.appearance?.welcome_message || 
                                agentManagementService?.generateWelcomeMessage(agentData) || 
                                'Hi, Was können wir für Sie tun?',
                appearance: {
                  ...getDefaultAgent()?.appearance,
                  ...agentData?.appearance
                }
              };

              setAgent(completeAgent);
              
              // Process and apply individual agent styling
              const styling = processAgentStyling(completeAgent);
              if (styling) {
                setAppliedStyling(styling);
                applyDynamicStyling(styling);
                console.log(`🎨 EMBED: Applied individual agent styling:`, styling);
              }
              
              // Update OpenAI config with agent settings
              updateOpenAIConfig({
                model: agentData?.model || 'gpt-4',
                systemPrompt: agentData?.system_prompt || 'Du bist ein hilfreicher AI-Assistent. Antworte auf Deutsch und sei freundlich und professionell.',
                streamEnabled: agentData?.streaming_enabled !== false,
                temperature: agentData?.temperature || 0.67,
                reasoningEffort: 'medium',
                verbosity: 'medium'
              });

              console.log(`🎯 EMBED: Successfully configured agent: ${agentData?.display_name || agentData?.name} (ID: ${agentData?.id})`);
            } else {
              throw new Error('Agent not found in database');
            }
          } catch (err) {
            console.warn(`⚠️ EMBED: Failed to load agent ${agentId}:`, err?.message);
            
            // Show clear error message but continue with default configuration
            setError(`Der angeforderte Agent (ID: ${agentId}) wurde nicht gefunden. Es wird eine Standard-Konfiguration verwendet.`);
            
            const defaultAgent = getDefaultAgent();
            setAgent(defaultAgent);
            
            // Apply default styling
            const defaultStyling = processAgentStyling(defaultAgent);
            if (defaultStyling) {
              setAppliedStyling(defaultStyling);
              applyDynamicStyling(defaultStyling);
            }
            
            updateOpenAIConfig({
              model: defaultAgent?.model,
              systemPrompt: defaultAgent?.system_prompt,
              streamEnabled: defaultAgent?.streaming_enabled,
              temperature: defaultAgent?.temperature,
              reasoningEffort: 'medium',
              verbosity: 'medium'
            });
            
            console.log(`🔄 EMBED: Using default agent configuration with custom styling`);
          }
        } else {
          console.log(`⚙️ EMBED: No agent ID provided, using default configuration`);
          
          // Use default agent with styling
          const defaultAgent = getDefaultAgent();
          setAgent(defaultAgent);
          
          // Apply default styling
          const defaultStyling = processAgentStyling(defaultAgent);
          if (defaultStyling) {
            setAppliedStyling(defaultStyling);
            applyDynamicStyling(defaultStyling);
          }
          
          updateOpenAIConfig({
            model: defaultAgent?.model,
            systemPrompt: defaultAgent?.system_prompt,
            streamEnabled: defaultAgent?.streaming_enabled,
            temperature: defaultAgent?.temperature,
            reasoningEffort: 'medium',
            verbosity: 'medium'
          });
        }

        setIsLoadingAgent(false);

        // Test OpenAI connection
        try {
          const connectionResult = await testConnection();
          if (!connectionResult?.connected) {
            setError('Verbindung zum KI-Service fehlgeschlagen. Bitte versuchen Sie es später erneut.');
          } else {
            console.log(`🤖 EMBED: OpenAI connection successful`);
          }
        } catch (connectError) {
          console.warn('🔌 EMBED: Connection test failed:', connectError?.message);
        }

        setIsInitialized(true);
        console.log(`🚀 EMBED: Widget initialization complete with individual styling`);
        
      } catch (err) {
        console.error('❌ EMBED: Failed to initialize chat widget:', err);
        setError('Chat-Initialisierung fehlgeschlagen. Bitte versuchen Sie es später erneut.');
        setIsLoadingAgent(false);
        setIsInitialized(true);
      }
    };

    initializeAgent();
  }, [agentId, testConnection, updateOpenAIConfig]);

  // Handle sending messages with better error handling
  const handleSendMessage = async (messageContent) => {
    if (!messageContent?.trim()) return;

    // Clear any previous errors
    clearError();
    setError(null);

    const userMessage = {
      content: messageContent,
      isUser: true,
      timestamp: Date.now()
    };

    setMessages(prev => [...prev, userMessage]);
    setStreamingMessage('');

    try {
      // Check if OpenAI is connected, if not, try to reconnect
      if (!openaiConnected) {
        const reconnectResult = await testConnection();
        if (!reconnectResult?.connected) {
          throw new Error('Unable to connect to AI service');
        }
      }

      // Use streaming for better user experience
      await sendStreamingMessage(
        messageContent,
        (chunk, fullContent) => {
          setStreamingMessage(fullContent);
        }
      );
      
      // Add final message after streaming completes
      if (lastResponse && !lastResponse?.error) {
        const botMessage = {
          content: lastResponse?.content || 'Ich habe Ihre Nachricht erhalten, konnte aber keine Antwort generieren. Bitte versuchen Sie es erneut.',
          isUser: false,
          timestamp: Date.now()
        };
        
        setMessages(prev => [...prev, botMessage]);
        setStreamingMessage('');
      } else if (lastResponse?.error) {
        const errorMessage = {
          content: 'Entschuldigung, bei der Bearbeitung Ihrer Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
          isUser: false,
          timestamp: Date.now()
        };
        
        setMessages(prev => [...prev, errorMessage]);
        setStreamingMessage('');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message in German
      const errorMessage = {
        content: 'Entschuldigung, bei der Bearbeitung Ihrer Anfrage ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.',
        isUser: false,
        timestamp: Date.now()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setStreamingMessage('');
    }
  };

  const handleInputSubmit = () => {
    if (inputMessage?.trim()) {
      handleSendMessage(inputMessage?.trim());
      setInputMessage('');
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleInputSubmit();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp)?.toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading state with agent ID information
  if (!isInitialized || isLoadingAgent) {
    return (
      <div className={getStyledClasses('flex items-center justify-center h-screen', 'chatBackground')}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-current border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={getStyledClasses('text-sm', 'textPrimary')}>Chat wird geladen...</p>
          {agentId && agentId !== 'default' && (
            <p className={getStyledClasses('text-xs mt-2', 'textMuted')}>Agent ID: {agentId}</p>
          )}
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !agent) {
    return (
      <div className={getStyledClasses('flex items-center justify-center h-screen p-4', 'chatBackground')}>
        <div className="text-center max-w-sm">
          <div className="w-12 h-12 rounded-full bg-red-900 flex items-center justify-center mx-auto mb-4">
            <AppIcon name="AlertCircle" size={24} color="#ef4444" />
          </div>
          <h3 className={getStyledClasses('font-medium mb-2', 'textPrimary')}>Chat konnte nicht geladen werden</h3>
          <p className={getStyledClasses('text-sm mb-4', 'textSecondary')}>{error}</p>
          {agentId && agentId !== 'default' && (
            <p className={getStyledClasses('text-xs mb-4', 'textMuted')}>Agent ID: {agentId}</p>
          )}
          <button
            onClick={() => window.location?.reload()}
            className={getStyledClasses('px-4 py-2 rounded-md transition-colors text-sm font-medium', 'buttonPrimary')}
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  const MessageBubble = ({ message, isUser }) => (
    <div className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}>
      {!isUser && (
        <div className="flex-shrink-0 mr-3">
          {appliedStyling?.botAvatar ? (
            <img 
              src={appliedStyling?.botAvatar} 
              alt="Bot Avatar" 
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className={getStyledClasses('w-8 h-8 rounded-full flex items-center justify-center', 'buttonPrimary')}>
              <AppIcon name="Bot" size={16} color="currentColor" />
            </div>
          )}
        </div>
      )}
      
      <div className={`max-w-xs px-4 py-3 rounded-2xl ${
        isUser 
          ? getStyledClasses('', 'userMessageBackground')
          : getStyledClasses('', 'messageBackground')
      }`} style={{ fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined }}>
        <div className="text-sm whitespace-pre-wrap">{message?.content}</div>
        <div className="text-xs mt-1 opacity-70">
          {formatTime(message?.timestamp)}
        </div>
      </div>
      
      {isUser && (
        <div className="flex-shrink-0 ml-3">
          {appliedStyling?.userAvatar ? (
            <img 
              src={appliedStyling?.userAvatar} 
              alt="User Avatar" 
              className="w-8 h-8 rounded-full object-cover"
            />
          ) : (
            <div className={getStyledClasses('w-8 h-8 rounded-full flex items-center justify-center', 'messageBackground')}>
              <AppIcon name="User" size={16} color="currentColor" />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const TypingIndicator = () => (
    <div className="flex justify-start mb-4">
      <div className="flex-shrink-0 mr-3">
        {appliedStyling?.botAvatar ? (
          <img 
            src={appliedStyling?.botAvatar} 
            alt="Bot Avatar" 
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className={getStyledClasses('w-8 h-8 rounded-full flex items-center justify-center', 'buttonPrimary')}>
            <AppIcon name="Bot" size={16} color="currentColor" />
          </div>
        )}
      </div>
      
      <div className={getStyledClasses('px-4 py-3 rounded-2xl', 'messageBackground')}>
        <div className="flex space-x-1">
          <div className={`w-2 h-2 rounded-full animate-bounce`} style={{ backgroundColor: appliedStyling?.textMuted || '#9ca3af' }}></div>
          <div className={`w-2 h-2 rounded-full animate-bounce`} style={{ backgroundColor: appliedStyling?.textMuted || '#9ca3af', animationDelay: '0.1s' }}></div>
          <div className={`w-2 h-2 rounded-full animate-bounce`} style={{ backgroundColor: appliedStyling?.textMuted || '#9ca3af', animationDelay: '0.2s' }}></div>
        </div>
      </div>
    </div>
  );

  const StreamingMessage = () => (
    <div className="flex justify-start mb-4">
      <div className="flex-shrink-0 mr-3">
        {appliedStyling?.botAvatar ? (
          <img 
            src={appliedStyling?.botAvatar} 
            alt="Bot Avatar" 
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className={getStyledClasses('w-8 h-8 rounded-full flex items-center justify-center', 'buttonPrimary')}>
            <AppIcon name="Bot" size={16} color="currentColor" />
          </div>
        )}
      </div>
      
      <div className={getStyledClasses('px-4 py-3 rounded-2xl max-w-xs', 'messageBackground')} style={{ fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined }}>
        <div className="text-sm whitespace-pre-wrap">
          {streamingMessage}
          <span className={`inline-block w-2 h-4 ml-1 animate-pulse`} style={{ backgroundColor: appliedStyling?.textPrimary || '#ffffff' }}></span>
        </div>
      </div>
    </div>
  );

  // Render full-screen chat interface for iframe embedding
  if (isInIframe) {
    return (
      <div className={getStyledClasses('h-screen flex flex-col overflow-hidden', 'chatBackground')} style={{ fontFamily: appliedStyling?.fontFamily !== 'system' ? appliedStyling?.fontFamily : undefined }}>
        {/* Header with Agent Information */}
        <div className={getStyledClasses('flex items-center justify-between p-4 border-b', 'headerBackground')}>
          <div className="flex items-center space-x-3">
            <div className={getStyledClasses('w-10 h-10 rounded-full flex items-center justify-center', 'buttonPrimary')}>
              <AppIcon name={appliedStyling?.bubbleIcon || "MessageCircle"} size={24} color="currentColor" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={getStyledClasses('font-semibold text-lg truncate', 'textPrimary')}>
                {agent?.display_name || agent?.name || 'AI Assistent'}
              </h3>
              {appliedStyling?.showTagline && appliedStyling?.taglineText && (
                <p className={getStyledClasses('text-sm truncate', 'textSecondary')}>
                  {appliedStyling?.taglineText}
                </p>
              )}
              {agentId && agentId !== 'default' && (
                <p className={getStyledClasses('text-xs font-mono', 'textMuted')}>
                  ID: {agentId?.slice(0, 8)}...
                </p>
              )}
            </div>
          </div>
        </div>
        {/* Messages Area */}
        <div className={getStyledClasses('flex-1 overflow-y-auto p-4', 'chatBackground')}>
          {/* Agent Loading Success Indicator */}
          {agent && agentId && agentId !== 'default' && !error && (
            <div className="text-center py-2 mb-4">
              <div className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-900 mb-1">
                <AppIcon name="CheckCircle" size={14} color="#10b981" />
              </div>
              <p className="text-xs text-green-400">Agent erfolgreich geladen</p>
            </div>
          )}

          {/* Welcome Message */}
          {messages?.length === 0 && agent?.welcome_message && (
            <div className="flex justify-start mb-6">
              <div className="flex-shrink-0 mr-3">
                {appliedStyling?.botAvatar ? (
                  <img 
                    src={appliedStyling?.botAvatar} 
                    alt="Bot Avatar" 
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className={getStyledClasses('w-8 h-8 rounded-full flex items-center justify-center', 'buttonPrimary')}>
                    <AppIcon name="Bot" size={16} color="currentColor" />
                  </div>
                )}
              </div>
              
              <div className={getStyledClasses('px-4 py-3 rounded-2xl max-w-sm', 'messageBackground')} style={{ fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined }}>
                <div className="text-sm whitespace-pre-wrap">{agent?.welcome_message}</div>
              </div>
            </div>
          )}

          {/* Conversation Starters */}
          {messages?.length === 0 && agent?.conversation_starters && agent?.conversation_starters?.length > 0 && (
            <div className="space-y-3 mb-6 max-w-sm">
              {agent?.conversation_starters?.map((starter, index) => (
                <button
                  key={index}
                  onClick={() => handleSendMessage(starter)}
                  className={getStyledClasses('block w-full text-left p-4 border rounded-2xl hover:opacity-80 transition-colors text-sm font-medium shadow-lg', 'messageBackground')}
                  style={{ 
                    fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined,
                    borderColor: appliedStyling?.borderColor 
                  }}
                  disabled={isTyping}
                >
                  {starter}
                </button>
              ))}
            </div>
          )}

          {/* Messages */}
          {messages?.map((message, index) => (
            <MessageBubble 
              key={index} 
              message={message} 
              isUser={message?.isUser} 
            />
          ))}

          {/* Streaming Message */}
          {streamingMessage && <StreamingMessage />}

          {/* Typing Indicator */}
          {isTyping && !streamingMessage && appliedStyling?.showTypingIndicator && <TypingIndicator />}

          {/* Connection/Configuration Errors */}
          {error && (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-900 mb-2">
                <AppIcon name="AlertTriangle" size={16} color="#f59e0b" />
              </div>
              <p className="text-sm text-yellow-400 mb-2">{error}</p>
              <p className="text-xs text-gray-500">Das System funktioniert normal, verwendet aber Standard-Einstellungen.</p>
            </div>
          )}
        </div>
        {/* Input Area */}
        <div className={getStyledClasses('p-4 border-t', 'headerBackground')}>
          <div className="flex items-center space-x-3 max-w-4xl mx-auto">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nachricht eingeben..."
                className={getStyledClasses('w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 text-sm', 'inputBackground')}
                style={{ 
                  fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined,
                  focusRingColor: appliedStyling?.primaryColor 
                }}
                disabled={isTyping}
              />
            </div>
            
            <button
              onClick={handleInputSubmit}
              disabled={!inputMessage?.trim() || isTyping}
              className={getStyledClasses('flex-shrink-0 p-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg', 'buttonPrimary')}
            >
              <AppIcon name="Send" size={16} />
            </button>
          </div>
          
          {!openaiConnected && (
            <p className="text-xs text-red-400 mt-2 text-center max-w-4xl mx-auto">Verbindung getrennt. Versuche erneut zu verbinden...</p>
          )}
        </div>
      </div>
    );
  }

  // Render floating widget for direct page access
  return (
    <div className="fixed bottom-6 right-6 z-50" style={{ fontFamily: appliedStyling?.fontFamily !== 'system' ? appliedStyling?.fontFamily : undefined }}>
      {/* Chat Widget */}
      <div className={`${getStyledClasses('rounded-3xl shadow-2xl border', 'chatBackground')} ${getStyledClasses('', 'border')} w-96 h-[600px] flex flex-col overflow-hidden`}>
        
        {/* Header */}
        <div className={getStyledClasses('flex items-center justify-between p-6 border-b', 'headerBackground')}>
          <div className="flex items-center space-x-3">
            <div className={getStyledClasses('w-10 h-10 rounded-full flex items-center justify-center', 'buttonPrimary')}>
              <AppIcon name={appliedStyling?.bubbleIcon || "MessageCircle"} size={24} color="currentColor" />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={getStyledClasses('font-semibold text-lg truncate', 'textPrimary')}>
                {agent?.display_name || agent?.name || 'AI Assistent'}
              </h3>
              {appliedStyling?.showTagline && appliedStyling?.taglineText && (
                <p className={getStyledClasses('text-sm truncate', 'textSecondary')}>
                  {appliedStyling?.taglineText}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className={getStyledClasses('flex-1 overflow-y-auto p-4', 'chatBackground')}>
          {/* Welcome Message */}
          {messages?.length === 0 && agent?.welcome_message && (
            <div className="flex justify-start mb-6">
              <div className="flex-shrink-0 mr-3">
                {appliedStyling?.botAvatar ? (
                  <img 
                    src={appliedStyling?.botAvatar} 
                    alt="Bot Avatar" 
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className={getStyledClasses('w-8 h-8 rounded-full flex items-center justify-center', 'buttonPrimary')}>
                    <AppIcon name="Bot" size={16} color="currentColor" />
                  </div>
                )}
              </div>
              
              <div className={getStyledClasses('px-4 py-3 rounded-2xl max-w-xs', 'messageBackground')} style={{ fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined }}>
                <div className="text-sm whitespace-pre-wrap">{agent?.welcome_message}</div>
              </div>
            </div>
          )}

          {/* Conversation Starters */}
          {messages?.length === 0 && agent?.conversation_starters && agent?.conversation_starters?.length > 0 && (
            <div className="space-y-3 mb-6">
              {agent?.conversation_starters?.map((starter, index) => (
                <button
                  key={index}
                  onClick={() => handleSendMessage(starter)}
                  className={getStyledClasses('block w-full text-left p-4 border rounded-2xl hover:opacity-80 transition-colors text-sm font-medium shadow-lg', 'messageBackground')}
                  style={{ 
                    fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined,
                    borderColor: appliedStyling?.borderColor 
                  }}
                  disabled={isTyping}
                >
                  {starter}
                </button>
              ))}
            </div>
          )}

          {/* Messages */}
          {messages?.map((message, index) => (
            <MessageBubble 
              key={index} 
              message={message} 
              isUser={message?.isUser} 
            />
          ))}

          {/* Streaming Message */}
          {streamingMessage && <StreamingMessage />}

          {/* Typing Indicator */}
          {isTyping && !streamingMessage && appliedStyling?.showTypingIndicator && <TypingIndicator />}

          {/* Connection Error */}
          {error && (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-yellow-900 mb-2">
                <AppIcon name="AlertTriangle" size={16} color="#f59e0b" />
              </div>
              <p className="text-sm text-yellow-400 mb-2">{error}</p>
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className={getStyledClasses('p-4 border-t', 'headerBackground')}>
          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e?.target?.value)}
                onKeyPress={handleKeyPress}
                placeholder="Nachricht eingeben..."
                className={getStyledClasses('w-full px-4 py-3 rounded-2xl focus:outline-none focus:ring-2 text-sm', 'inputBackground')}
                style={{ 
                  fontSize: appliedStyling?.fontSize ? `${appliedStyling?.fontSize}px` : undefined,
                  focusRingColor: appliedStyling?.primaryColor 
                }}
                disabled={isTyping}
              />
            </div>
            
            <button
              onClick={handleInputSubmit}
              disabled={!inputMessage?.trim() || isTyping}
              className={getStyledClasses('flex-shrink-0 p-3 rounded-2xl disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg', 'buttonPrimary')}
            >
              <AppIcon name="Send" size={16} />
            </button>
          </div>
          
          {!openaiConnected && (
            <p className="text-xs text-red-400 mt-2">Verbindung getrennt. Versuche erneut zu verbinden...</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmbedChatWidget;